import styled from '@emotion/styled';
import { FC, useEffect, useState } from 'react';
import { useReactiveVar, useQuery, useMutation, useLazyQuery } from '@apollo/client';
import {
  FormLabel as _FormLabel,
  CardActions as _CardActions,
  CardContent as _CardContent,
  CardMedia as _CardMedia,
  Grid,
  Typography,
} from '@material-ui/core';
import _HelpIcon from '@material-ui/icons/Help';
import { Chart, ChartScopeRequest } from '@kaizenplatform/kaizen-chartjs';
import { DateTime } from 'luxon';

import { lastAccessedOrganizationVar } from '../../cache';
import globalState from '../../operations/commands';
import {
  GoogleSearchConsoleSite,
  GetChartDocument,
  LinkSearchConsoleAccountDocument,
  GetSearchConsoleSitesDocument,
  GetSearchConsoleSitesQuery,
  GetSearchConsoleSitesQueryVariables,
} from '../../generated/graphql';
import { useGoogleOauth } from '../../hooks/useGoogleOauth';
import { COLOR_TEXT_MAIN } from '../../theme/colors';
import LoadingButton from '../component/primitive/LoadingButton';
import Loading from '../component/compound/Loading';
import _PreviewImage from '../../view/component/compound/PreviewImage';
import MainImageFile from '../../assets/site_search_analytics.png';
import GoogleSearchConsolePageTableChart from '../component/compound/SiteSearchAnalytics/GoogleSearchConsolePageTableChart';
import GoogleSearchConsoleOverviewChart from '../component/compound/SiteSearchAnalytics/GoogleSearchConsoleOverviewChart';
import AvailableSitesDrawer from '../component/compound/SiteSearchAnalytics/AvailableSitesDrawer';

type Props = {
  className?: string;
};

const SiteSearchAnalyticsPage: FC<Props> = ({ className }) => {
  const organization = useReactiveVar(lastAccessedOrganizationVar)!;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [linkButtonLoading, setLinkButtonLoading] = useState(false);
  const [linkedSite, setLinkedSite] = useState<GoogleSearchConsoleSite | null>();
  const [accountId, setAccountId] = useState<string | null>(null);
  const [tableChart, setTableChart] = useState<Chart | null>(null);
  const { code, authenticate } = useGoogleOauth();
  const [pageTableChartDate, setPageTableChartDate] = useState<string>(
    // SearchConsoleのデータは3日遅れで取得しているため、月初3日間はデータが無いため
    DateTime.local().minus({ days: 3 }).startOf('month').toSQLDate(),
  );
  const {
    data: sitesData,
    loading: getSitesLoading,
    refetch: refetchSites,
  } = useQuery<GetSearchConsoleSitesQuery, GetSearchConsoleSitesQueryVariables>(GetSearchConsoleSitesDocument, {
    context: { clientName: 'search_console' },
    errorPolicy: 'all',
    variables: {
      organization_id: organization.id,
    },
    onCompleted: (data) => {
      // open drawer if there is no site linked
      if (!!accountId && data.sites.every((site: GoogleSearchConsoleSite) => !site.linked)) {
        setAccountId(null); // 組織切り替え時にdrawerがいきなり開かないように、開いたら即nullに変更しておく
        setLinkButtonLoading(false);
        setDrawerOpen(true);
      }
    },
    onError: (error) => {
      setLinkedSite(null);
      setTableChart(null);
      /////
      //  LIST_SITES-ACCOUNT_NOT_INTEGRATED
      //  UNAUTHORIZED_ORGANIZATION_ERROR
      ////
      // TODO move this up in ErrorLink
      // error.message is "Response not successful: Received status code 400"
      // error.result.code is only accessible inbetween apollo links
      if (error.message !== 'LIST_SITES-ACCOUNT_NOT_INTEGRATED') {
        const message =
          error.message === 'UNAUTHORIZED_ORGANIZATION_ERROR'
            ? 'この組織ではこの機能は利用できません'
            : 'Googleとの連携に失敗しました';
        globalState.setErrors({
          message: message,
          name: 'NonFatalError',
          context: 'search_console',
        });
      }
    },
  });

  const [linkAccount] = useMutation(LinkSearchConsoleAccountDocument, {
    context: { clientName: 'search_console' },
    variables: {
      input: {
        organization_id: organization.id,
        redirect_uri: window.location.origin,
        code: code,
      },
    },
    // Func linkGoogleSearchConsoleAccountAndGetSites - Linkしたら必ずサイト一覧を取得する
    onCompleted: (data) => {
      setAccountId(data.account.account_id);
      refetchSites({ organization_id: organization.id });
    },
    onError: (error) => {
      // CREATE_ACCOUNT-GOOGLE_FETCH_TOKEN_API_ERROR
      globalState.setErrors({
        message: error.message,
        name: 'NonFatalError',
        context: 'search_console',
      });
    },
  });

  const authenticateAndLinkAccount = () => {
    setLinkButtonLoading(true);
    authenticate()
      .then(() => linkAccount())
      .catch((error) => {
        setLinkButtonLoading(false);
        const message =
          error.message === 'Google authentication is canceled'
            ? '連携をキャンセルしました'
            : 'Googleとの連携に失敗しました';
        globalState.setErrors({
          message: message,
          name: 'NonFatalError',
          context: { clientName: 'search_console' },
        });
      });
  };

  const COLUMN_NAMES = {
    page_text_data: 'ページ',
    clicks: 'クリック数',
    clicks_last_month: 'クリック数(前月)',
    divide_clicks: '進捗率(前月比)',
  };
  const [getTableChart, { loading: getTableChartLoading }] = useLazyQuery(GetChartDocument, {
    context: { clientName: 'dataplatform' },
    onCompleted: (data) => {
      const convertedColumns = data?.chart.table?.columns.map(
        (column: string) => COLUMN_NAMES[column as keyof typeof COLUMN_NAMES] || '',
      );
      if (data.chart.table)
        setTableChart({
          ...data.chart,
          table: {
            ...data.chart.table!,
            columns: convertedColumns,
          },
        });
    },
  });

  useEffect(() => {
    // 連携済のサイトが存在する場合
    if (sitesData && sitesData.sites.some((site: GoogleSearchConsoleSite) => site.linked)) {
      const datetime = DateTime.fromISO(pageTableChartDate);
      const site = sitesData.sites.find((site: GoogleSearchConsoleSite) => site.linked)!;
      setLinkedSite(site);
      getTableChart({
        variables: {
          id: site.page_resource_key,
          identifierType: 'resource_key',
          scope: {
            from: datetime.startOf('month').toISO(),
            to: datetime.endOf('month').toISO(),
          } as ChartScopeRequest,
          breakdowns: [
            {
              breakdown: 'site',
              filter: [site.site_url],
            },
            {
              breakdown: 'date',
              filter: [pageTableChartDate],
            },
          ],
        },
      });
    } else {
      setLinkedSite(null);
    }
  }, [sitesData, pageTableChartDate]);

  const toggleDrawer = (open: boolean) => {
    setDrawerOpen(open);
  };

  const handleChangePageTableChartDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageTableChartDate(event.target.value);
  };

  return (
    <Root className={className}>
      <Container>
        <Header>
          <Title>検索流入分析(β)</Title>
        </Header>
        {sitesData && (
          <AvailableSitesDrawer
            sites={sitesData?.sites}
            organizationId={organization.id}
            accountId={accountId}
            setAccountId={setAccountId}
            open={drawerOpen}
            toggleDrawer={toggleDrawer}
            refetchSites={refetchSites}
          />
        )}
        {getSitesLoading ? (
          <Loading />
        ) : !linkedSite ? (
          //not linked yet
          <Content>
            <EmptyContent>
              <img src={MainImageFile} />
              <MainLabel>レポートの準備をはじめましょう</MainLabel>
              <SubLabel>検索流入データの分析をするために、Google Search Consoleと連携してください。</SubLabel>
              <LinkButton onClick={authenticateAndLinkAccount} loading={linkButtonLoading}>
                Google Search Consoleと連携する
              </LinkButton>
            </EmptyContent>
          </Content>
        ) : tableChart?.table && tableChart?.table.rows && tableChart?.table.rows.length === 0 ? (
          // Backend in the process to prepare charts
          <Content>
            <EmptyContent>
              <img src={MainImageFile} />
              <MainLabel>レポート分析中です...</MainLabel>
              <SubLabel>
                連携済みサイト: {sitesData?.sites?.find((site: GoogleSearchConsoleSite) => site.linked)?.site_url}
              </SubLabel>
              <SubLabel>レポート分析が完了するまでしばらくお待ち下さい (最大24時間要します)</SubLabel>
              <LinkButton disabled>既に連携済みです</LinkButton>
            </EmptyContent>
          </Content>
        ) : (
          <div>
            <Grid container spacing={4}>
              <Grid item lg={12} sm={12} xs={12}>
                <GoogleSearchConsoleOverviewChart linkedSite={linkedSite} />
              </Grid>
              <Grid item lg={12} sm={12} xs={12}>
                <GoogleSearchConsolePageTableChart
                  chart={tableChart}
                  loading={getTableChartLoading}
                  selectedDate={pageTableChartDate}
                  handleChangeDate={handleChangePageTableChartDate}
                />
              </Grid>
            </Grid>
          </div>
        )}
      </Container>
    </Root>
  );
};

const Root = styled('div')({});

const Container = styled('div')(`
  padding: 24px;
`);

const Content = styled('div')(`
  height: 100%;
  width: 100%;
`);

const Header = styled('div')(`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`);

const Title = styled(Typography)(`
  font-weight: 700;
  font-size: 24px;
  color: ${COLOR_TEXT_MAIN};
`);

const EmptyContent = styled('div')(`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  & > img {
    padding: 40px;
  }
  & > div {
    margin-top: 24px;
    font-size: 24px;
    font-weight: bold;
  }
`);

const MainLabel = styled('h3')(`
  font-size: 24px;
  margin-bottom: 24px;
`);

const SubLabel = styled('p')(`
  font-size: 10px;
  font-weight: 500;
  margin-bottom: 10px;
`);

const StyledButton = styled(LoadingButton)(`
  color: #FFFFFF;
  height: 32px;
  width: 128px;
  font-size: 14px;
  background: ${COLOR_TEXT_MAIN};
  font-weight: 800;
  border-color: ${COLOR_TEXT_MAIN};
  border-radius: 4px;
  :hover {
    background: #707889;
    border-color: #707889;
  }
  :disabled {
    background: #E6E7E8;
    color: rgba(75, 85, 106, 0.24);
    border: rgba(75, 85, 106, 0.24);
  }
`);

const LinkButton = styled(StyledButton)(`
  margin-top: 24px;
  width: 360px;
`);

export default SiteSearchAnalyticsPage;
